/**
 * Support Staff Dashboard
 * Landing page for non-teacher staff (cafeteria workers, bus drivers, recess monitors)
 * Allows location-based behavior tracking for any student in the school
 */

import React, { useState, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { studentsApi, type Student } from '@/services/api';
import { BehaviorTracking } from '@/components/teacher/BehaviorTracking';
import { StudentPhotoCard } from '@/components/support-staff/StudentPhotoCard';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePageTitle } from '@/hooks/usePageTitle';
import { getContainerStyle } from '@/styles/containers';
import { useNameFormat } from '@/utils/nameFormat';
import { formatGradeLong } from '@/utils/gradeLabels';
import { usePagination } from '@/hooks/usePagination';
import { useStableTotal } from '@/hooks/useStableTotal';
import { useDebounce } from '@/hooks/useDebounce';
import { Pagination } from '@/components/Pagination';

interface RecentStudent {
  id: string;
  name: string;
  photoFileId: string | null;
  photoUrl: string | null;
}

export default function SupportStaffDashboard() {
  usePageTitle('Dashboard');
  // Hooks
  const { isMobile } = useMediaQuery();
  const { formatStudentName } = useNameFormat();
  const containerStyle = getContainerStyle('full', isMobile);

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [selectedStudent, setSelectedStudent] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [behaviorModalOpen, setBehaviorModalOpen] = useState(false);
  const [recentStudents, setRecentStudents] = useState<RecentStudent[]>([]);
  const [selectedGrade, setSelectedGrade] = useState<number | 'all'>('all');
  const [sortOption, setSortOption] = useState('lastName-asc');

  const [sortBy, sortOrder] = sortOption.split('-') as [string, 'asc' | 'desc'];

  // Pagination
  const pagination = usePagination({ defaultPageSize: 50 });

  // Reset page when search, grade filter, or sort changes
  useEffect(() => {
    pagination.resetPage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedGrade, sortOption, pagination.resetPage]);

  // Fetch paginated students (server-side search, filtering, sorting)
  const {
    data: paginatedResponse,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['students', 'support-staff', {
      search: debouncedSearch,
      gradeLevel: selectedGrade,
      sortBy,
      sortOrder,
      limit: pagination.pageSize,
      offset: pagination.offset,
    }],
    queryFn: async () => {
      const response = await studentsApi.getPaginated({
        search: debouncedSearch || undefined,
        gradeLevel: selectedGrade !== 'all' ? selectedGrade : undefined,
        limit: pagination.pageSize,
        offset: pagination.offset,
        sortBy,
        sortOrder,
      });
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    placeholderData: keepPreviousData,
  });

  const students = paginatedResponse?.data ?? [];
  const responseTotal = paginatedResponse?.total ?? 0;
  const availableGrades = paginatedResponse?.availableGrades ?? [];

  // Stable total for display — doesn't drop to 0 during search
  const totalStudents = useStableTotal(responseTotal, !!debouncedSearch);

  // Load recent students from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('recentStudents_supportStaff');
    if (stored) {
      setRecentStudents(JSON.parse(stored));
    }
  }, []);

  // Add student to recent list
  const addToRecent = (student: Student) => {
    const recent: RecentStudent = {
      id: student.id,
      name: formatStudentName(student.firstName, student.lastName),
      photoFileId: student.photoFileId ?? null,
      photoUrl: student.photoUrl,
    };

    const updated = [recent, ...recentStudents.filter((s) => s.id !== student.id)].slice(0, 10); // Keep last 10

    setRecentStudents(updated);
    localStorage.setItem('recentStudents_supportStaff', JSON.stringify(updated));
  };

  // Handle student click
  const handleStudentClick = (student: Student) => {
    addToRecent(student);
    setSelectedStudent({
      id: student.id,
      name: formatStudentName(student.firstName, student.lastName),
    });
    setBehaviorModalOpen(true);
  };

  return (
    <>
      <div style={containerStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>Log Behavior</h1>
          <p style={subtitleStyle}>Select a student to log a behavior</p>
        </div>

        {/* Search, Filter & Sort Toolbar */}
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }} aria-hidden="true" />
              <input
                type="text"
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px var(--primary-color)'; }}
                onBlur={(e) => { e.target.style.boxShadow = 'none'; }}
                aria-label="Search students"
                style={searchInputStyle}
              />
              {searchQuery && (
                <button type="button" aria-label="Clear search" onClick={() => setSearchQuery('')} style={clearButtonStyle}>
                  <X size={16} />
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <select
                className="form-input form-input-compact"
                value={selectedGrade === 'all' ? 'all' : String(selectedGrade)}
                onChange={(e) => setSelectedGrade(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                style={{ flex: 1 }}
                aria-label="Filter by grade"
              >
                <option value="all">All Grades</option>
                {availableGrades.map((grade) => (
                  <option key={grade} value={String(grade)}>{formatGradeLong(grade)}</option>
                ))}
              </select>
              <select
                className="form-input form-input-compact"
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                style={{ flex: 1 }}
                aria-label="Sort students"
              >
                <option value="lastName-asc">Last Name (A-Z)</option>
                <option value="lastName-desc">Last Name (Z-A)</option>
                <option value="firstName-asc">First Name (A-Z)</option>
                <option value="firstName-desc">First Name (Z-A)</option>
                <option value="gradeLevel-asc">Grade (Low-High)</option>
                <option value="gradeLevel-desc">Grade (High-Low)</option>
              </select>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1.5rem' }}>
            <div style={{ position: 'relative', width: 'min(280px, 100%)' }}>
              <Search size={18} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }} aria-hidden="true" />
              <input
                type="text"
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={(e) => { e.target.style.boxShadow = '0 0 0 2px var(--primary-color)'; }}
                onBlur={(e) => { e.target.style.boxShadow = 'none'; }}
                aria-label="Search students"
                style={searchInputStyle}
              />
              {searchQuery && (
                <button type="button" aria-label="Clear search" onClick={() => setSearchQuery('')} style={clearButtonStyle}>
                  <X size={16} />
                </button>
              )}
            </div>
            <select
              className="form-input form-input-compact"
              value={selectedGrade === 'all' ? 'all' : String(selectedGrade)}
              onChange={(e) => setSelectedGrade(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              aria-label="Filter by grade"
            >
              <option value="all">All Grades</option>
              {availableGrades.map((grade) => (
                <option key={grade} value={String(grade)}>{formatGradeLong(grade)}</option>
              ))}
            </select>
            <select
              className="form-input form-input-compact"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              aria-label="Sort students"
            >
              <option value="lastName-asc">Last Name (A-Z)</option>
              <option value="lastName-desc">Last Name (Z-A)</option>
              <option value="firstName-asc">First Name (A-Z)</option>
              <option value="firstName-desc">First Name (Z-A)</option>
              <option value="gradeLevel-asc">Grade (Low-High)</option>
              <option value="gradeLevel-desc">Grade (High-Low)</option>
            </select>
          </div>
        )}

        {/* Recent Students */}
        {recentStudents.length > 0 && !searchQuery && (
          <div style={recentSectionStyle}>
            <h2 style={sectionTitleStyle}>Recent Students</h2>
            <div style={recentGridStyle}>
              {recentStudents.map((student) => (
                <StudentPhotoCard
                  key={student.id}
                  student={student}
                  onClick={() => {
                    // Recent students already have name/photo — open modal directly
                    setSelectedStudent({ id: student.id, name: student.name });
                    setBehaviorModalOpen(true);
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* All Students */}
        <div style={studentsSectionStyle}>
          <h2 style={sectionTitleStyle}>
            {searchQuery && selectedGrade !== 'all'
              ? `Search Results — ${formatGradeLong(selectedGrade)} (${responseTotal} of ${totalStudents})`
              : searchQuery
                ? `Search Results (${responseTotal} of ${totalStudents})`
                : selectedGrade !== 'all'
                  ? `${formatGradeLong(selectedGrade)} Students (${responseTotal})`
                  : `All Students (${responseTotal})`}
          </h2>

          {isError ? (
            <div style={errorStateStyle}>
              Failed to load students. Please refresh the page.
              {error && <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Error: {error.message}</div>}
            </div>
          ) : isLoading ? (
            <div style={loadingStyle}>Loading students...</div>
          ) : students.length === 0 ? (
            <div style={emptyStateStyle}>{searchQuery ? 'No students found' : 'No students available'}</div>
          ) : (
            <div style={studentsGridStyle}>
              {students.map((student) => (
                <StudentPhotoCard
                  key={student.id}
                  student={{
                    id: student.id,
                    name: formatStudentName(student.firstName, student.lastName),
                    photoFileId: student.photoFileId ?? null,
                    photoUrl: student.photoUrl ?? null,
                  }}
                  onClick={() => handleStudentClick(student)}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          <Pagination
            page={pagination.page}
            totalPages={Math.ceil(responseTotal / pagination.pageSize)}
            pageSize={pagination.pageSize}
            pageSizeOptions={pagination.pageSizeOptions}
            total={responseTotal}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
          />
        </div>
      </div>

      {/* Behavior Tracking Modal (Location-Only Mode) */}
      {selectedStudent && (
        <BehaviorTracking
          isOpen={behaviorModalOpen}
          onClose={() => setBehaviorModalOpen(false)}
          studentId={selectedStudent.id}
          studentName={selectedStudent.name}
          mode="single"
          // NO blockId = forces location-only mode
        />
      )}
    </>
  );
}

// Styles (inline for simplicity)
const headerStyle: React.CSSProperties = {
  marginBottom: '2rem',
};

const titleStyle: React.CSSProperties = {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: 'var(--text-primary)',
  marginBottom: '0.5rem',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '1rem',
  color: 'var(--text-secondary)',
};

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.625rem 2.5rem',
  fontSize: '0.875rem',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius)',
  outline: 'none',
  backgroundColor: 'var(--bg-primary)',
  color: 'var(--text-primary)',
};

const clearButtonStyle: React.CSSProperties = {
  position: 'absolute',
  right: '0.5rem',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-secondary)',
  padding: '0.25rem',
  display: 'flex',
  alignItems: 'center',
};

const recentSectionStyle: React.CSSProperties = {
  marginBottom: '2rem',
  paddingBottom: '2rem',
  borderBottom: '1px solid var(--border-color)',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: '600',
  color: 'var(--text-primary)',
  marginBottom: '1rem',
};

const recentGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
  gap: '1rem',
  maxWidth: '800px',
};

const studentsSectionStyle: React.CSSProperties = {
  marginBottom: '2rem',
};

const studentsGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
  gap: '1.5rem',
};

const loadingStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '3rem',
  color: 'var(--text-secondary)',
};

const emptyStateStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '3rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};

const errorStateStyle: React.CSSProperties = {
  textAlign: 'center',
  padding: '3rem',
  color: 'var(--error-color)',
  backgroundColor: 'rgba(220, 53, 69, 0.1)',
  borderRadius: 'var(--radius)',
  border: '1px solid var(--error-color)',
};
